import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const ProjectEstimator = () => {
  const [estimatorData, setEstimatorData] = useState({
    projectType: '',
    complexity: '',
    features: [],
    timeline: ''
  });

  const [showEstimate, setShowEstimate] = useState(false);

  const projectTypes = [
    { value: 'mvp', label: 'MVP Development', basePrice: 25000 },
    { value: 'saas', label: 'SaaS Platform', basePrice: 75000 },
    { value: 'enterprise', label: 'Enterprise Solution', basePrice: 150000 },
    { value: 'mobile', label: 'Mobile Application', basePrice: 50000 },
    { value: 'web', label: 'Web Application', basePrice: 40000 }
  ];

  const complexityLevels = [
    { value: 'simple', label: 'Simple', multiplier: 1 },
    { value: 'moderate', label: 'Moderate', multiplier: 1.5 },
    { value: 'complex', label: 'Complex', multiplier: 2 },
    { value: 'enterprise', label: 'Enterprise-grade', multiplier: 3 }
  ];

  const featureOptions = [
    { id: 'auth', label: 'User Authentication & Authorization', cost: 5000 },
    { id: 'payment', label: 'Payment Integration', cost: 8000 },
    { id: 'analytics', label: 'Analytics Dashboard', cost: 10000 },
    { id: 'api', label: 'API Development', cost: 12000 },
    { id: 'admin', label: 'Admin Panel', cost: 7000 },
    { id: 'notifications', label: 'Push Notifications', cost: 4000 },
    { id: 'chat', label: 'Real-time Chat', cost: 9000 },
    { id: 'search', label: 'Advanced Search', cost: 6000 }
  ];

  const timelineOptions = [
    { value: 'urgent', label: 'Urgent (1-2 months)', multiplier: 1.3 },
    { value: 'standard', label: 'Standard (3-6 months)', multiplier: 1 },
    { value: 'flexible', label: 'Flexible (6+ months)', multiplier: 0.9 }
  ];

  const calculateEstimate = () => {
    const selectedProject = projectTypes?.find(p => p?.value === estimatorData?.projectType);
    const selectedComplexity = complexityLevels?.find(c => c?.value === estimatorData?.complexity);
    const selectedTimeline = timelineOptions?.find(t => t?.value === estimatorData?.timeline);

    if (!selectedProject || !selectedComplexity || !selectedTimeline) {
      return null;
    }

    let basePrice = selectedProject?.basePrice;
    basePrice *= selectedComplexity?.multiplier;

    const featuresTotal = estimatorData?.features?.reduce((sum, featureId) => {
      const feature = featureOptions?.find(f => f?.id === featureId);
      return sum + (feature ? feature?.cost : 0);
    }, 0);

    const subtotal = basePrice + featuresTotal;
    const total = subtotal * selectedTimeline?.multiplier;

    return {
      basePrice,
      featuresTotal,
      timelineAdjustment: subtotal * (selectedTimeline?.multiplier - 1),
      total,
      duration: selectedTimeline?.label
    };
  };

  const handleFeatureToggle = (featureId) => {
    setEstimatorData(prev => ({
      ...prev,
      features: prev?.features?.includes(featureId)
        ? prev?.features?.filter(id => id !== featureId)
        : [...prev?.features, featureId]
    }));
  };

  const handleEstimate = () => {
    if (estimatorData?.projectType && estimatorData?.complexity && estimatorData?.timeline) {
      setShowEstimate(true);
    }
  };

  const estimate = showEstimate ? calculateEstimate() : null;

  return (
    <section className="py-12 md:py-16 lg:py-20 bg-gradient-to-br from-accent/5 via-background to-primary/5">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8 md:mb-12">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold font-headline text-foreground mb-3 md:mb-4">
            Project Cost Estimator
          </h2>
          <p className="text-sm md:text-base lg:text-lg text-muted-foreground font-body max-w-2xl mx-auto">
            Get a preliminary estimate for your project. This tool provides a rough range based on typical project parameters.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
          <div className="lg:col-span-2 bg-card border border-border rounded-xl p-6 md:p-8 shadow-lg">
            <div className="space-y-6 md:space-y-8">
              <div>
                <Select
                  label="Project Type"
                  placeholder="Select project type"
                  options={projectTypes}
                  value={estimatorData?.projectType}
                  onChange={(value) => setEstimatorData(prev => ({ ...prev, projectType: value }))}
                  required
                />
              </div>

              <div>
                <Select
                  label="Project Complexity"
                  placeholder="Select complexity level"
                  options={complexityLevels}
                  value={estimatorData?.complexity}
                  onChange={(value) => setEstimatorData(prev => ({ ...prev, complexity: value }))}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-4">
                  Additional Features
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                  {featureOptions?.map((feature) => (
                    <Checkbox
                      key={feature?.id}
                      label={`${feature?.label}`}
                      checked={estimatorData?.features?.includes(feature?.id)}
                      onChange={() => handleFeatureToggle(feature?.id)}
                    />
                  ))}
                </div>
              </div>

              <div>
                <Select
                  label="Timeline Preference"
                  placeholder="Select timeline"
                  options={timelineOptions}
                  value={estimatorData?.timeline}
                  onChange={(value) => setEstimatorData(prev => ({ ...prev, timeline: value }))}
                  required
                />
              </div>

              <Button
                variant="default"
                size="lg"
                fullWidth
                iconName="Calculator"
                iconPosition="left"
                onClick={handleEstimate}
                disabled={!estimatorData?.projectType || !estimatorData?.complexity || !estimatorData?.timeline}
              >
                Calculate Estimate
              </Button>
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="bg-card border border-border rounded-xl p-6 md:p-8 shadow-lg sticky top-24">
              <h3 className="text-lg md:text-xl font-semibold font-headline text-foreground mb-4 md:mb-6">
                Estimated Investment
              </h3>

              {!showEstimate ? (
                <div className="text-center py-8 md:py-12">
                  <Icon name="Calculator" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
                  <p className="text-sm md:text-base text-muted-foreground font-body">
                    Fill in the project details to see your estimated investment range
                  </p>
                </div>
              ) : estimate ? (
                <div className="space-y-4 md:space-y-6">
                  <div className="space-y-3">
                    <div className="flex justify-between items-center pb-3 border-b border-border">
                      <span className="text-sm text-muted-foreground font-body">Base Project</span>
                      <span className="text-sm font-medium text-foreground font-body">
                        ${(estimate?.basePrice / 1000)?.toFixed(0)}k
                      </span>
                    </div>
                    
                    {estimate?.featuresTotal > 0 && (
                      <div className="flex justify-between items-center pb-3 border-b border-border">
                        <span className="text-sm text-muted-foreground font-body">Additional Features</span>
                        <span className="text-sm font-medium text-foreground font-body">
                          ${(estimate?.featuresTotal / 1000)?.toFixed(0)}k
                        </span>
                      </div>
                    )}
                    
                    {estimate?.timelineAdjustment !== 0 && (
                      <div className="flex justify-between items-center pb-3 border-b border-border">
                        <span className="text-sm text-muted-foreground font-body">Timeline Adjustment</span>
                        <span className={`text-sm font-medium font-body ${estimate?.timelineAdjustment > 0 ? 'text-warning' : 'text-success'}`}>
                          {estimate?.timelineAdjustment > 0 ? '+' : ''}${(estimate?.timelineAdjustment / 1000)?.toFixed(0)}k
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="bg-primary/5 rounded-lg p-4 md:p-6">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-base md:text-lg font-semibold text-foreground font-headline">Total Estimate</span>
                      <span className="text-2xl md:text-3xl font-bold text-primary font-headline">
                        ${(estimate?.total / 1000)?.toFixed(0)}k
                      </span>
                    </div>
                    <p className="text-xs md:text-sm text-muted-foreground font-body">
                      Duration: {estimate?.duration}
                    </p>
                  </div>

                  <div className="bg-muted/50 rounded-lg p-4">
                    <div className="flex items-start space-x-2">
                      <Icon name="Info" size={16} color="var(--color-muted-foreground)" className="flex-shrink-0 mt-0.5" />
                      <p className="text-xs md:text-sm text-muted-foreground font-body">
                        This is a preliminary estimate. Final pricing depends on detailed requirements and scope.
                      </p>
                    </div>
                  </div>

                  <Button
                    variant="outline"
                    size="default"
                    fullWidth
                    iconName="MessageSquare"
                    iconPosition="left"
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  >
                    Discuss This Estimate
                  </Button>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProjectEstimator;